import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ShieldAlert } from 'lucide-react';
import { trackQuoteEvent } from '../lib/quoteAnalytics';

/**
 * WinQuoteForm
 * ----------------------------------------------------------------------
 * Embeds the WinQuote-supplied quote form inside a sandboxed iframe so its
 * Bootstrap/JS does not collide with Utmost's Tailwind UI. The iframe source is
 * served by our backend at /api/winquote/form?lang=&product= which injects
 * Utmost brand colours.
 *
 * If the WinQuote account is ever expired/unavailable, the backend
 * /api/winquote/status probe returns { active: false } and we show a graceful
 * "request a personalized quote" fallback instead of WinQuote's raw error.
 *
 * Props:
 *   product — 'life' (default) | 'critical'
 *   lang    — 'en' (default) | 'fr'
 *   apiUrl  — backend base URL (defaults to REACT_APP_BACKEND_URL)
 */
export default function WinQuoteForm({
  product = 'life',
  lang = 'en',
  apiUrl = process.env.REACT_APP_BACKEND_URL,
}) {
  const src = useMemo(
    () => `${apiUrl}/api/winquote/form?lang=${lang}&product=${product}`,
    [apiUrl, lang, product]
  );
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState(null); // null = checking, true/false = result
  const loadCount = useRef(0);

  useEffect(() => {
    let cancelled = false;
    fetch(`${apiUrl}/api/winquote/status`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setActive(d.active !== false); })
      .catch(() => { if (!cancelled) setActive(true); }); // fail-open
    return () => { cancelled = true; };
  }, [apiUrl]);

  const onIframeLoad = () => {
    setLoaded(true);
    loadCount.current += 1;
    if (loadCount.current === 1) {
      trackQuoteEvent('quote_iframe_loaded', { tab: product });
    } else {
      trackQuoteEvent('quote_submitted', { tab: product });
    }
  };

  const title = product === 'critical' ? 'Critical Illness Quote' : 'Life Insurance Quote';

  return (
    <div className="w-full" data-testid={`winquote-form-${product}`}>
      <div className="bg-gradient-to-r from-[#003D5C] to-[#0077B6] text-white px-5 py-4 rounded-t-xl border-b-4 border-[#D4AF37]">
        <h3 className="font-bold text-lg sm:text-xl">{title}</h3>
        <p className="text-blue-100 text-xs sm:text-sm mt-1">
          Live quotes from Canada's leading carriers — powered by WinQuote®.
          Results are estimates; an Utmost advisor will confirm before binding.
        </p>
      </div>

      <div className="relative bg-white rounded-b-xl border border-t-0 border-gray-200 shadow-sm overflow-hidden">
        {active === false ? (
          /* Graceful fallback — WinQuote account unavailable */
          <div className="p-8 text-center" data-testid={`winquote-fallback-${product}`}>
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#F0F7FF] mb-4">
              <ShieldAlert className="h-7 w-7 text-[#0077B6]" />
            </div>
            <h4 className="text-lg font-bold text-[#003D5C] mb-2">
              Live quotes are temporarily unavailable
            </h4>
            <p className="text-sm text-gray-600 max-w-md mx-auto mb-6">
              Our instant quoting engine is being updated. A licensed Utmost advisor can
              prepare a personalized {product === 'critical' ? 'critical illness' : 'life insurance'} quote
              for you right away — usually within one business day.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/contact"
                data-testid={`winquote-fallback-contact-${product}`}
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[#D4AF37] text-[#003D5C] font-bold hover:bg-[#B8962E] transition-colors"
              >
                Request a Personalized Quote
              </Link>
              <a
                href="tel:18772399060"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border-2 border-[#0077B6] text-[#0077B6] font-semibold hover:bg-[#F0F7FF] transition-colors"
              >
                <Phone className="h-4 w-4" /> 1-877-239-9060
              </a>
            </div>
          </div>
        ) : (
          <>
            {!loaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-10" data-testid="winquote-loading">
                <div className="text-center">
                  <div className="inline-block w-10 h-10 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin mb-3"></div>
                  <p className="text-sm text-gray-600">Loading quote engine…</p>
                </div>
              </div>
            )}
            {active !== null && (
              <iframe
                key={src}
                src={src}
                title="WinQuote Insurance Quote Form"
                onLoad={onIframeLoad}
                className="w-full"
                style={{ minHeight: 720, border: 0, display: 'block' }}
                data-testid="winquote-iframe"
              />
            )}
          </>
        )}
      </div>

      <p className="text-xs text-gray-500 mt-3 text-center px-4">
        Quotes provided by WinQuote®/Equisoft Inc. Premiums are estimates and may change after carrier underwriting.
        For a binding quote, an Utmost Financial licensed advisor will follow up — typically within one business day.
      </p>
    </div>
  );
}
